export interface CheckoutPriceCalculation {
  paidAttendeeCount: number;
  waitlistedAttendeeCount: number;
  baseAmount: number;
  voucherDiscount: number;
  codeDiscount: number;
  adminDiscount: number;
  codeDiscountPerSeat: number;
  adminDiscountPerSeat: number;
  paidSeatsAfterVoucher: number;
  total: number;
}

// Minimal interface for price calculation - only needs what we actually use
export interface PricingAttendee {
  isSelected: boolean;
  isWaitlist: boolean;
}

export interface DiscountInfo {
  type: "percentage" | "fixed_amount";
  value: number;
}

function calculatePerSeat(
  eventPrice: number,
  discount: DiscountInfo | null,
): number {
  if (!discount || discount.value <= 0) return 0;
  if (discount.type === "percentage") {
    return eventPrice * (discount.value / 100);
  }
  return Math.min(discount.value, eventPrice);
}

export function calculateCheckoutPrice<T extends PricingAttendee>(
  attendees: T[],
  eventPrice: number,
  voucherQuantity: number,
  codeDiscount: DiscountInfo | null,
  adminDiscount: DiscountInfo | null,
): CheckoutPriceCalculation {
  const paidAttendees = attendees.filter((a) => a.isSelected && !a.isWaitlist);
  const waitlistedAttendees = attendees.filter(
    (a) => a.isSelected && a.isWaitlist,
  );

  const paidAttendeeCount = paidAttendees.length;
  const waitlistedAttendeeCount = waitlistedAttendees.length;

  const baseAmount = paidAttendeeCount * eventPrice;
  const voucherDiscount =
    Math.min(voucherQuantity, paidAttendeeCount) * eventPrice;
  const paidSeatsAfterVoucher = Math.max(
    0,
    paidAttendeeCount - Math.min(voucherQuantity, paidAttendeeCount),
  );

  let codeDiscountPerSeat = calculatePerSeat(eventPrice, codeDiscount);
  let adminDiscountPerSeat = calculatePerSeat(eventPrice, adminDiscount);

  // Cap: total per-seat discount cannot exceed eventPrice
  const totalPerSeat = codeDiscountPerSeat + adminDiscountPerSeat;
  if (totalPerSeat > eventPrice && eventPrice > 0) {
    const ratio = eventPrice / totalPerSeat;
    codeDiscountPerSeat = Math.round(codeDiscountPerSeat * ratio * 100) / 100;
    adminDiscountPerSeat =
      Math.round(adminDiscountPerSeat * ratio * 100) / 100;
  }

  const codeDiscountTotal = codeDiscountPerSeat * paidSeatsAfterVoucher;
  const adminDiscountTotal = adminDiscountPerSeat * paidSeatsAfterVoucher;

  const total = Math.max(
    0,
    baseAmount - voucherDiscount - codeDiscountTotal - adminDiscountTotal,
  );

  return {
    paidAttendeeCount,
    waitlistedAttendeeCount,
    baseAmount,
    voucherDiscount,
    codeDiscount: codeDiscountTotal,
    adminDiscount: adminDiscountTotal,
    codeDiscountPerSeat,
    adminDiscountPerSeat,
    paidSeatsAfterVoucher,
    total,
  };
}
